import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, FileText, FolderOpen, ClipboardList, ListChecks, Users
} from 'lucide-react';
import api from '../services/api';
import './SearchResults.css';

const TYPE_ICONS = {
  reports:   FileText,
  documents: FolderOpen,
  plans:     ClipboardList,
  tasks:     ListChecks,
  users:     Users,
};

function SearchResults() {
  const { t } = useTranslation(['navigation', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeType, setActiveType] = useState('');
  const [searchInput, setSearchInput] = useState(query);

  useEffect(() => {
    setSearchInput(query);
    if (query.length >= 2) {
      fetchResults(query, '');
      setActiveType('');
    }
    // eslint-disable-next-line
  }, [query]);

  const fetchResults = async (q, type) => {
    setLoading(true);
    try {
      const params = { full: true };
      if (type) params.type = type;
      const res = await api.globalSearch(q, params);
      setResults(res.data);
    } catch {
      setResults(null);
    }
    setLoading(false);
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    if (query.length >= 2) {
      fetchResults(query, type);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`);
    }
  };

  return (
    <div className="search-results-page">
      <div className="search-results-header">
        <h1><Search size={24} /> {t('navigation:search.title')}</h1>
        <form className="search-results-form" onSubmit={handleSearch}>
          <Search size={18} className="search-form-icon" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('navigation:search.placeholder')}
          />
          <button type="submit">{t('navigation:search.find')}</button>
        </form>
      </div>

      {query && (
        <p className="search-results-summary">
          {loading
            ? t('navigation:search.searching')
            : results
              ? t('navigation:search.resultsFound', { count: results.total, query })
              : ''
          }
        </p>
      )}

      {/* Вкладки по типам */}
      <div className="search-type-tabs">
        <button
          className={`search-type-tab ${!activeType ? 'active' : ''}`}
          onClick={() => handleTypeChange('')}
        >
          {t('navigation:search.all')}
        </button>
        {Object.entries(TYPE_ICONS).map(([key, Icon]) => (
          <button
            key={key}
            className={`search-type-tab ${activeType === key ? 'active' : ''}`}
            onClick={() => handleTypeChange(key)}
          >
            <Icon size={14} />
            {t(`navigation:searchCategories.${key}`)}
          </button>
        ))}
      </div>

      {/* Результаты */}
      {results && !loading && Object.entries(results.results).map(([type, data]) => (
        data.items.length > 0 && (
          <div key={type} className="search-results-group">
            <h2 className="search-results-group-title">
              {React.createElement(TYPE_ICONS[type] || FileText, { size: 18 })}
              {t(`navigation:searchCategories.${type}`, { defaultValue: type })} ({data.count})
            </h2>
            <div className="search-results-list">
              {data.items.map(item => (
                <Link key={`${type}-${item.id}`} to={item.url} className="search-result-card">
                  <div className="search-result-card-main">
                    <div className="search-result-card-title">{item.title}</div>
                    <div className="search-result-card-subtitle">{item.subtitle}</div>
                  </div>
                  {item.status && (
                    <span className="search-result-card-status">{item.status}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )
      ))}

      {results && results.total === 0 && !loading && (
        <div className="search-results-empty">
          <Search size={48} />
          <h3>{t('navigation:search.noResults', { query })}</h3>
          <p>{t('navigation:search.noResultsHint')}</p>
        </div>
      )}

      {!query && (
        <div className="search-results-empty">
          <Search size={48} />
          <h3>{t('navigation:search.enterQuery')}</h3>
          <p>{t('navigation:search.enterQueryHint')}</p>
        </div>
      )}
    </div>
  );
}

export default SearchResults;
